import React, { useEffect, useRef, useState } from 'react';

/**
 * Compteur animé : affiche un nombre qui monte de 0 (ou de la valeur précédente)
 * jusqu'à `value` en `duration` millisecondes, avec une courbe ease-out.
 *
 * Utilisé sur le dashboard pour donner du dynamisme aux KPI à l'arrivée des
 * données. Aucune dépendance externe : requestAnimationFrame natif.
 *
 * Props :
 *  - value     : nombre cible (les non-nombres sont remplacés par 0)
 *  - duration  : durée de l'animation en ms (défaut 800)
 *  - formatter : fonction (n) => string pour formater l'affichage final
 *                (par défaut: nombre brut). Exemples :
 *                  formatGNF                → "1 250 000 GNF"
 *                  n => n.toLocaleString()  → "1 250 000"
 *
 * Note : si le composant est ré-rendu avec une nouvelle `value`, l'animation
 * repart de la valeur précédemment affichée, pas de 0 (transition fluide).
 */
export default function CountUp({ value, duration = 800, formatter }) {
  const target = Number(value) || 0;
  const [display, setDisplay] = useState(target);
  // displayRef miroite display sans figurer dans les deps de useEffect
  const displayRef = useRef(target);
  const fromRef    = useRef(target);
  const rafRef     = useRef(null);

  useEffect(() => {
    const from = fromRef.current;
    const to   = target;
    if (from === to) return;

    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - t, 3);
      const current = Math.round(from + (to - from) * eased);
      displayRef.current = current;
      setDisplay(current);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(step);
      } else {
        fromRef.current = to;
      }
    };
    rafRef.current = requestAnimationFrame(step);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // Si une nouvelle animation démarre avant la fin, on repart de la
      // dernière valeur affichée pour rester fluide.
      fromRef.current = displayRef.current;
    };
  }, [target, duration]);

  return <>{formatter ? formatter(display) : display}</>;
}
